import { useFragment } from '@apollo/client/react';
import { createGetSuggestedSiteNamesPlugin } from '@atlassiansox/cross-flow-plugins';
import TrelloCrossFlowProvider from '@atlassiansox/cross-flow-support/trello';
import {
  useCallback,
  type FunctionComponent,
  type PropsWithChildren,
} from 'react';
import { IntlProvider } from 'react-intl';

import { getApiGatewayUrl } from '@trello/api-gateway';
import { getAnalyticsClientMemberIdProxy } from '@trello/atlassian-analytics';
import { getMemberIdWithFallback } from '@trello/authentication';
import { environment } from '@trello/config';
import { ErrorBoundary } from '@trello/error-boundaries';
import { sendErrorEvent } from '@trello/error-reporting';
import { currentLocale } from '@trello/locale';
import { EMPTY_PII_STRING } from '@trello/privacy';
import { quickLoadSharedState } from '@trello/quickload-shared-state';
import { useSharedStateSelector } from '@trello/shared-state';

import { getSuggestedSiteNames } from './crossFlowEssentials';
import { CrossFlowProviderFragmentDoc } from './CrossFlowProviderFragment.generated';

export const CrossFlowProvider: FunctionComponent<
  PropsWithChildren<object>
> = ({ children }) => {
  const isQuickLoading = useSharedStateSelector(
    quickLoadSharedState,
    useCallback((state) => state.isLoading, []),
  );

  const memberId = getMemberIdWithFallback();

  const { data, complete } = useFragment({
    fragment: CrossFlowProviderFragmentDoc,
    fragmentName: 'CrossFlowProvider',
    from: !isQuickLoading
      ? {
          id: memberId,
          __typename: 'Member',
        }
      : null,
  });

  // The codegen fragment hook returned `undefined` until the read was
  // complete; this gate preserves that and may be removable later.
  const fragmentData = complete ? data : undefined;

  const me = fragmentData;
  const ent = me?.enterprises ?? [];
  const teams = me?.organizations ?? [];

  const member = {
    id: me?.id || '',
    idPremOrgsAdmin: me?.idPremOrgsAdmin || [],
    fullName: me?.fullName || EMPTY_PII_STRING,
    enterprises: ent,
  };

  const env = environment === 'prod' ? 'production' : 'staging';

  // Remove the trailing slash from the base url if present
  const edgePrefix = getApiGatewayUrl().replace(/\/gateway\/api$/, '');

  const suggestedSiteNamesPlugin = createGetSuggestedSiteNamesPlugin(async () =>
    getSuggestedSiteNames(ent, teams, member),
  );

  const onError = useCallback((err: Error) => {
    sendErrorEvent(err, {
      tags: {
        ownershipArea: 'trello-web-eng',
      },
      extraData: {
        component: 'CrossFlowProvider',
      },
    });
  }, []);

  return (
    <ErrorBoundary
      tags={{
        ownershipArea: 'trello-web-eng',
        feature: 'Cross Flow Provider',
      }}
      sendCrashEvent={false}
    >
      <IntlProvider
        key={currentLocale}
        data-test-language={currentLocale}
        locale={currentLocale}
      >
        <TrelloCrossFlowProvider
          // @ts-expect-error
          analyticsClient={getAnalyticsClientMemberIdProxy()}
          locale={currentLocale}
          edgePrefix={edgePrefix}
          isAaMastered={true}
          env={env}
          plugins={[suggestedSiteNamesPlugin]}
          onError={onError}
        >
          {children}
        </TrelloCrossFlowProvider>
      </IntlProvider>
    </ErrorBoundary>
  );
};
